import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import type { Manual, Section, SubSection, Block, BlockLayout, BlockData, TableTemplate } from "../models/ManualAuthoring";
import { authoringSchema } from "../models/AuthoringSchema";
import { canInsert, canMove } from "../rules/blockRuleEngine";

type AuthoringState = {
  schema: typeof authoringSchema;
  manual: Manual;
  presentManual: Manual;
  past: Manual[];
  future: Manual[];
  manualTitle: string;
  selectedBlockId: string | null;
  selectedSectionId: string | null;
  selectedSubSectionId: string | null;

  loadManual: (json: Manual) => void;
  createSection: (title: string) => void;
  insertSectionAt: (title: string, index: number) => void;
  createSubSection: (sectionId: string, title: string) => void;
  insertSubSectionAt: (sectionId: string, title: string, index: number) => void;
  updateSectionTitle: (sectionId: string, title: string) => void;
  updateSubSectionTitle: (sectionId: string, subSectionId: string, title: string) => void;
  insertBlock: (targetSectionId: string, targetSubSectionId: string | null, layout: BlockLayout) => void;
  insertBlockAt: (
    targetSectionId: string,
    targetSubSectionId: string | null,
    layout: BlockLayout,
    index: number
  ) => void;
  insertTableBlockAt: (
    targetSectionId: string,
    targetSubSectionId: string | null,
    tableTemplate: TableTemplate,
    index: number
  ) => void;
  updateBlock: (blockId: string, data: Partial<BlockData>) => void;
  deleteBlock: (blockId: string) => void;
  moveBlockUp: (blockId: string) => void;
  moveBlockDown: (blockId: string) => void;
  moveSectionUp: (sectionId: string) => void;
  moveSectionDown: (sectionId: string) => void;
  deleteSection: (sectionId: string) => void;
  moveSubSectionUp: (sectionId: string, subSectionId: string) => void;
  moveSubSectionDown: (sectionId: string, subSectionId: string) => void;
  deleteSubSection: (sectionId: string, subSectionId: string) => void;
  selectSection: (sectionId: string | null) => void;
  selectSubSection: (sectionId: string, subSectionId: string | null) => void;
  selectBlock: (blockId: string | null) => void;
  setManualTitle: (title: string) => void;
  undo: () => void;
  redo: () => void;
  saveManual: () => Manual;
};

const nextId = (prefix: string) => `${prefix}-${uuidv4()}`;

export const createEmptyBlockData = (layout: BlockLayout): BlockData => {
  switch (layout) {
    case "paragraph":
    case "paragraphIndented":
    case "subheading":
    case "step":
    case "subStep":    
    case "note":
    case "warning":
    case "caution":
      return { textParts: [] };

    case "bulletList":
    case "numberList":
      return { items: [] };

    case "figure":
      return { svgId: 0, folderPath: "", caption: "" };

    case "figureLeftTextRight":
    case "figureRightTextLeft":
      return {
        figure: { svgId: 0, folderPath: "", caption: "" },
        contentBlocks: [],
      };

    case "table":
      return {
        tableId: undefined,
        templateName: "new_table_template",
        displayTitle: "New Table Template",
        width: "100%",
        alignment: "left",
        headerRepeat: true,
        keepTogether: false,
        schemaVersion: 1,
        columns: [],
        rows: [],
      };

    default:
      throw new Error(`Unsupported block layout: ${layout}`);
  }
};


const findBlockContainer = (sections: Section[], blockId: string) => {
  for (const section of sections) {
    if (section.blocks) {
      const idx = section.blocks.findIndex((b) => b.id === blockId);
      if (idx >= 0) {
        return { list: section.blocks, index: idx, sectionId: section.id, subSectionId: null };
      }
    }
    if (section.subSections) {
      for (const sub of section.subSections) {
        const idx = sub.blocks.findIndex((b) => b.id === blockId);
        if (idx >= 0) {
          return { list: sub.blocks, index: idx, sectionId: section.id, subSectionId: sub.id };
        }
      }
    }
  }
  return null;
};

const HISTORY_LIMIT = 5;

const applyMutation = (
  set: (fn: (state: AuthoringState) => Partial<AuthoringState> | AuthoringState) => void,
  mutate: (present: Manual) => Manual
) => {
  set((state) => {
    const nextManual = mutate(state.presentManual);
    const nextPast = [...state.past, state.presentManual];
    const past = nextPast.length > HISTORY_LIMIT ? nextPast.slice(-HISTORY_LIMIT) : nextPast;
    return {
      past,
      future: [],
      presentManual: nextManual,
      manual: nextManual,
    };
  });
};

export const useManualAuthoringStore = create<AuthoringState>()(
  persist(
    (set, get) => ({
      schema: authoringSchema,
      presentManual: { sections: [] },
      manual: { sections: [] },
      past: [],
      future: [],
      manualTitle: "",
      selectedBlockId: null,
      selectedSectionId: null,
      selectedSubSectionId: null,

      loadManual: (json) =>
        set({
          presentManual: json,
          manual: json,
          past: [],
          future: [],
        }),
      setManualTitle: (title) => set({ manualTitle: title }),

      createSection: (title) =>
        applyMutation(set, (present) => {
          const section: Section = {
            id: nextId("section"),
            title,
            blocks: [],
          };
          return { sections: [...present.sections, section] };
        }),
      insertSectionAt: (title, index) =>
        applyMutation(set, (present) => {
          const section: Section = {
            id: nextId("section"),
            title,
            blocks: [],
          };
          const sections = [...present.sections];
          const idx = Math.max(0, Math.min(index, sections.length));
          sections.splice(idx, 0, section);
          return { sections };
        }),

      createSubSection: (sectionId, title) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            if (section.id !== sectionId) return section;
            const newSub: SubSection = {
              id: nextId("subsection"),
              title,
              blocks: [],
            };
            return {
              ...section,
              subSections: section.subSections ? [...section.subSections, newSub] : [newSub],
            };
          });
          return { sections };
        }),
      insertSubSectionAt: (sectionId, title, index) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            if (section.id !== sectionId) return section;
            const newSub: SubSection = {
              id: nextId("subsection"),
              title,
              blocks: [],
            };
            const subs = section.subSections ? [...section.subSections] : [];
            const idx = Math.max(0, Math.min(index, subs.length));
            subs.splice(idx, 0, newSub);
            return { ...section, subSections: subs };
          });
          return { sections };
        }),
      updateSectionTitle: (sectionId, title) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) =>
            section.id === sectionId ? { ...section, title } : section
          );
          return { sections };
        }),

      updateSubSectionTitle: (sectionId, subSectionId, title) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            if (section.id !== sectionId || !section.subSections) return section;
            const subSections = section.subSections.map((sub) =>
              sub.id === subSectionId ? { ...sub, title } : sub
            );
            return { ...section, subSections };
          });
          return { sections };
        }),

      insertBlock: (targetSectionId, targetSubSectionId, layout) =>
        applyMutation(set, (present) => {
          const block: Block = {
            id: nextId("block"),
            layout,
            data: createEmptyBlockData(layout),
          };
          const sections = present.sections.map((section) => {
            if (section.id !== targetSectionId) return section;
            if (targetSubSectionId) {
              const subSections = (section.subSections || []).map((sub) =>
                sub.id === targetSubSectionId ? { ...sub, blocks: [...sub.blocks, block] } : sub
              );
              return { ...section, subSections };
            }
            const blocks = section.blocks ? [...section.blocks, block] : [block];
            return { ...section, blocks };
          });
          return { sections };
        }),
      insertBlockAt: (targetSectionId, targetSubSectionId, layout, index) =>
        applyMutation(set, (present) => {
          const block: Block = {
            id: nextId("block"),
            layout,
            data: createEmptyBlockData(layout),
          };

          const sections = present.sections.map((section) => {
            if (section.id !== targetSectionId) return section;

            // SUBSECTION LEVEL
            if (targetSubSectionId) {
              const subSections = (section.subSections || []).map((sub) => {
                if (sub.id !== targetSubSectionId) return sub;

                const blocks = [...sub.blocks];
                const idx = Math.max(0, Math.min(index, blocks.length));

                // 🔥 RULE VALIDATION
                if (!canInsert(blocks, layout, idx)) {
                  return sub;
                }

                blocks.splice(idx, 0, block);
                return { ...sub, blocks };
              });

              return { ...section, subSections };
            }

            // SECTION LEVEL
            const blocks = section.blocks ? [...section.blocks] : [];
            const idx = Math.max(0, Math.min(index, blocks.length));

            // 🔥 RULE VALIDATION
            if (!canInsert(blocks, layout, idx)) {
              return section;
            }

            blocks.splice(idx, 0, block);
            return { ...section, blocks };
          });

          return { sections };
        }),
      insertTableBlockAt: (
        targetSectionId,
        targetSubSectionId,
        tableTemplate,
        index
      ) =>
        applyMutation(set, (present) => {
          const snapshot = JSON.parse(JSON.stringify(tableTemplate)) as TableTemplate;
          const block: Block = {
            id: nextId("block"),
            layout: "table",
            data: snapshot,
          };

          const sections = present.sections.map((section) => {
            if (section.id !== targetSectionId) return section;

            if (targetSubSectionId) {
              const subSections = (section.subSections || []).map((sub) => {
                if (sub.id !== targetSubSectionId) return sub;

                const blocks = [...sub.blocks];
                const idx = Math.max(0, Math.min(index, blocks.length));

                if (!canInsert(blocks, "table", idx)) {
                  return sub;
                }

                blocks.splice(idx, 0, block);
                return { ...sub, blocks };
              });

              return { ...section, subSections };
            }

            const blocks = section.blocks ? [...section.blocks] : [];
            const idx = Math.max(0, Math.min(index, blocks.length));

            if (!canInsert(blocks, "table", idx)) {
              return section;
            }

            blocks.splice(idx, 0, block);
            return { ...section, blocks };
          });

          return { sections };
        }),

      updateBlock: (blockId, data) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            const updateBlocks = (blocks: Block[]) =>
              blocks.map((b) => (b.id === blockId ? { ...b, data: { ...b.data, ...data } } : b));
            const updatedSection: Section = { ...section };
            if (section.blocks) updatedSection.blocks = updateBlocks(section.blocks);
            if (section.subSections) {
              updatedSection.subSections = section.subSections.map((sub) => ({
                ...sub,
                blocks: updateBlocks(sub.blocks),
              }));
            }
            return updatedSection;
          });
          return { sections };
        }),

      deleteBlock: (blockId) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            const updatedSection: Section = { ...section };
            if (section.blocks) {
              updatedSection.blocks = section.blocks.filter((b) => b.id !== blockId);
            }
            if (section.subSections) {
              updatedSection.subSections = section.subSections.map((sub) => ({
                ...sub,
                blocks: sub.blocks.filter((b) => b.id !== blockId),
              }));
            }
            return updatedSection;
          });
          return { sections };
        }),

      moveBlockUp: (blockId) =>
        applyMutation(set, (present) => {
          const container = findBlockContainer(present.sections, blockId);
          if (!container || container.index === 0) return present;

          const list = [...container.list];

          // 🔥 RULE VALIDATION
          if (!canMove(list, container.index, container.index - 1)) {
            return present;
          }

          [list[container.index - 1], list[container.index]] = [
            list[container.index],
            list[container.index - 1],
          ];

          const sections = present.sections.map((section) => {
            if (section.id !== container.sectionId) return section;

            if (container.subSectionId) {
              const subSections = (section.subSections || []).map((sub) =>
                sub.id === container.subSectionId
                  ? { ...sub, blocks: list }
                  : sub
              );
              return { ...section, subSections };
            }

            return { ...section, blocks: list };
          });

          return { sections };
        }),

      moveBlockDown: (blockId) =>
        applyMutation(set, (present) => {
          const container = findBlockContainer(present.sections, blockId);
          if (!container || container.index === container.list.length - 1)
            return present;

          const list = [...container.list];

          // 🔥 RULE VALIDATION
          if (!canMove(list, container.index, container.index + 1)) {
            return present;
          }

          [list[container.index], list[container.index + 1]] = [
            list[container.index + 1],
            list[container.index],
          ];

          const sections = present.sections.map((section) => {
            if (section.id !== container.sectionId) return section;

            if (container.subSectionId) {
              const subSections = (section.subSections || []).map((sub) =>
                sub.id === container.subSectionId
                  ? { ...sub, blocks: list }
                  : sub
              );
              return { ...section, subSections };
            }

            return { ...section, blocks: list };
          });

          return { sections };
        }),

      moveSectionUp: (sectionId) =>
        applyMutation(set, (present) => {
          const idx = present.sections.findIndex((s) => s.id === sectionId);
          if (idx <= 0) return present;
          const sections = [...present.sections];
          [sections[idx - 1], sections[idx]] = [sections[idx], sections[idx - 1]];
          return { sections };
        }),

      moveSectionDown: (sectionId) =>
        applyMutation(set, (present) => {
          const idx = present.sections.findIndex((s) => s.id === sectionId);
          if (idx < 0 || idx === present.sections.length - 1) return present;
          const sections = [...present.sections];
          [sections[idx], sections[idx + 1]] = [sections[idx + 1], sections[idx]];
          return { sections };
        }),

      deleteSection: (sectionId) =>
        applyMutation(set, (present) => {
          const sections = present.sections.filter((s) => s.id !== sectionId);
          return { sections };
        }),

      moveSubSectionUp: (sectionId, subSectionId) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            if (section.id !== sectionId || !section.subSections) return section;
            const idx = section.subSections.findIndex((s) => s.id === subSectionId);
            if (idx <= 0) return section;
            const subSections = [...section.subSections];
            [subSections[idx - 1], subSections[idx]] = [subSections[idx], subSections[idx - 1]];
            return { ...section, subSections };
          });
          return { sections };
        }),

      moveSubSectionDown: (sectionId, subSectionId) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            if (section.id !== sectionId || !section.subSections) return section;
            const idx = section.subSections.findIndex((s) => s.id === subSectionId);
            if (idx < 0 || idx === section.subSections.length - 1) return section;
            const subSections = [...section.subSections];
            [subSections[idx], subSections[idx + 1]] = [subSections[idx + 1], subSections[idx]];
            return { ...section, subSections };
          });
          return { sections };
        }),

      deleteSubSection: (sectionId, subSectionId) =>
        applyMutation(set, (present) => {
          const sections = present.sections.map((section) => {
            if (section.id !== sectionId || !section.subSections) return section;
            const subSections = section.subSections.filter((s) => s.id !== subSectionId);
            return { ...section, subSections };
          });
          return { sections };
        }),

      selectSection: (sectionId) =>
        set({
          selectedSectionId: sectionId,
          selectedSubSectionId: null,
          selectedBlockId: null,
        }),

      selectSubSection: (sectionId, subSectionId) =>
        set({
          selectedSectionId: sectionId,
          selectedSubSectionId: subSectionId,
          selectedBlockId: null,
        }),

      selectBlock: (blockId) => set({ selectedBlockId: blockId }),

      undo: () =>
        set((state) => {
          if (state.past.length === 0) return state;
          const previous = state.past[state.past.length - 1];
          const past = state.past.slice(0, -1);
          return {
            presentManual: previous,
            manual: previous,
            past,
            future: [state.presentManual, ...state.future],
          };
        }),

      redo: () =>
        set((state) => {
          if (state.future.length === 0) return state;
          const next = state.future[0];
          const future = state.future.slice(1);
          return {
            presentManual: next,
            manual: next,
            past: [...state.past, state.presentManual],
            future,
          };
        }),

      saveManual: () => get().presentManual,
    }),
    {
      name: "niquill-manual-authoring-v2",
      partialize: (state) => ({
        presentManual: state.presentManual,
        past: state.past,
        future: state.future,
      }),
    }
  )
);
